import React, { useEffect, useState } from "react";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { exportToExcel } from "../../utils/exportExcel";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Users, CalendarDays, CheckSquare, TrendingUp, Download } from "lucide-react";

const COLORS = ["#1a2332", "#38b6e8", "#10b981", "#f59e0b", "#ef4444"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/reports/overview").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const [attRes, lvRes, taskRes] = await Promise.all([
        api.get("/attendance"),
        api.get("/leaves"),
        api.get("/tasks"),
      ]);
      const calcBreak = (breaks = []) =>
        Math.round(breaks.filter((b) => b.start && b.end)
          .reduce((a, b) => a + (new Date(b.end) - new Date(b.start)) / 60000, 0));
      const fmtT = (iso) => iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";

      const attSheet = attRes.data.map((r) => ({
        Employee: r.employee_name, Date: r.date,
        "Clock In": fmtT(r.clock_in), "Clock Out": fmtT(r.clock_out),
        "Break (mins)": calcBreak(r.breaks), "Active Hours": r.active_hours || 0,
        Status: r.clock_out ? "Completed" : r.clock_in ? "Active" : "Absent",
      }));
      const lvSheet = lvRes.data.map((l) => ({
        Employee: l.employee_name, "Start Date": l.start_date, "End Date": l.end_date,
        Reason: l.reason, Status: l.status, "Rejection Reason": l.rejection_reason || "",
        Submitted: new Date(l.created_at).toLocaleDateString("en-IN"),
      }));
      const taskSheet = taskRes.data.map((t) => ({
        Title: t.title, "Assigned To": t.assigned_to_name,
        Priority: t.priority, "Due Date": t.due_date || "", Status: t.status,
        Created: new Date(t.created_at).toLocaleDateString("en-IN"),
      }));
      exportToExcel(
        [{ data: attSheet, name: "Attendance" }, { data: lvSheet, name: "Leaves" }, { data: taskSheet, name: "Tasks" }],
        "AppDroid_EMS_All_Reports"
      );
      toast.success("All reports exported successfully!");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 h-64 animate-pulse" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const leaveChartData = [
    { name: "Pending", value: data?.leave_stats?.pending || 0 },
    { name: "Approved", value: data?.leave_stats?.approved || 0 },
    { name: "Rejected", value: data?.leave_stats?.rejected || 0 },
  ];

  const taskChartData = [
    { name: "Todo", value: data?.task_stats?.todo || 0 },
    { name: "In Progress", value: data?.task_stats?.in_progress || 0 },
    { name: "Done", value: data?.task_stats?.done || 0 },
  ];

  const pieColors = ["#f59e0b", "#10b981", "#ef4444"];
  const taskColors = ["#94a3b8", "#38b6e8", "#10b981"];

  return (
    <Layout>
      <div className="p-8 page-enter">
        <div className="flex items-center justify-between mb-7">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Reports & Analytics</h1>
            <p className="text-slate-500 text-sm mt-1">Visual summary of leaves, attendance and task performance</p>
          </div>
          <Button
            onClick={handleExportAll}
            disabled={exporting}
            className="bg-[#1a2332] hover:bg-[#2c3e50] text-white gap-2"
            data-testid="export-all-reports-btn"
          >
            <Download size={16} />
            {exporting ? "Exporting..." : "Export All Reports"}
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Users, label: "Total Employees", value: data?.overview.total_employees, color: "text-[#38b6e8]", bg: "bg-sky-50" },
            { icon: TrendingUp, label: "Present Today", value: data?.overview.today_attendance, color: "text-emerald-600", bg: "bg-emerald-50" },
            { icon: CalendarDays, label: "Pending Leaves", value: data?.overview.pending_leaves, color: "text-amber-600", bg: "bg-amber-50" },
            { icon: CheckSquare, label: "Active Tasks", value: data?.overview.active_tasks, color: "text-violet-600", bg: "bg-violet-50" },
          ].map(({ icon: Icon, label, value, color, bg }) => (
            <div key={label} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3 stat-card-hover">
              <div className={`p-2.5 rounded-xl ${bg}`}><Icon size={18} className={color} /></div>
              <div>
                <p className="text-slate-500 text-xs">{label}</p>
                <p className="text-2xl font-bold text-[#1a2332]">{value ?? 0}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Attendance Trend */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="attendance-trend-chart">
            <h3 className="font-semibold text-slate-900 mb-5">7-Day Attendance Trend</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data?.attendance_trend} barSize={24}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false}
                  tickFormatter={(v) => new Date(v).toLocaleDateString("en-IN", { month: "short", day: "numeric" })} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="present" fill="#38b6e8" radius={[4, 4, 0, 0]} name="Present" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Leave Status */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="leave-stats-chart">
            <h3 className="font-semibold text-slate-900 mb-5">Leave Status Distribution</h3>
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="60%" height={220}>
                <PieChart>
                  <Pie data={leaveChartData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                    {leaveChartData.map((_, i) => <Cell key={i} fill={pieColors[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v}`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3 flex-1">
                {leaveChartData.map((item, i) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full shrink-0" style={{ background: pieColors[i] }} />
                    <span className="text-sm text-slate-600 flex-1">{item.name}</span>
                    <span className="text-sm font-bold text-slate-800">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Task Status */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 lg:col-span-2" data-testid="task-stats-chart">
            <h3 className="font-semibold text-slate-900 mb-5">Task Status Overview</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={taskChartData} barSize={40} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: "#64748b" }} tickLine={false} axisLine={false} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} name="Tasks">
                  {taskChartData.map((_, i) => <Cell key={i} fill={taskColors[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </Layout>
  );
}
